import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { blogAPI } from '../services/api';
import SEO from '../components/SEO';

const BlogPost = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      const articleData = await blogAPI.getBySlug(slug);
      setArticle(articleData);
      
      const allArticles = await blogAPI.getAll(0, 100);
      const related = allArticles.filter(a => a.id !== articleData.id).slice(0, 3);
      setRelatedArticles(related);
    } catch (error) {
      console.error('Error loading article:', error);
      toast.error('Article non trouvé');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-900 animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-blue-900 mb-4">Article non trouvé</h1>
          <Link to="/blog">
            <Button>Retour au blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {article && (
        <SEO
          title={`${article.title} | Blog DEBECO`}
          description={article.excerpt || article.content?.substring(0, 155)}
          canonical={`/blog/${slug}`}
        />
      )}
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/blog" className="inline-flex items-center text-blue-200 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au blog
          </Link>
          
          <div className="max-w-4xl">
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                {article.category}
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-6">{article.title}</h1>
            
            <div className="flex flex-wrap items-center gap-6 text-blue-200">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{new Date(article.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <span>{article.author}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Featured Image */}
            <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={article.image} 
                alt={article.title}
                className="w-full h-96 object-cover"
                width="896"
                height="384"
              />
            </div>

            {/* Content */}
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <div className="prose prose-lg max-w-none">
                  {article.content.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="text-gray-700 mb-6 leading-relaxed text-lg">
                      {paragraph}
                    </p>
                  ))}
                </div>

                {/* CTA within article */}
                <div className="mt-12 p-6 bg-gradient-to-br from-blue-50 to-emerald-50 rounded-xl border-2 border-emerald-200">
                  <h3 className="text-xl font-bold text-blue-900 mb-3">Besoin d'un débarras professionnel ?</h3>
                  <p className="text-gray-700 mb-4">Contactez DEBECO pour un devis gratuit et une intervention rapide.</p>
                  <Link to="/contact">
                    <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white">
                      Demander un devis gratuit
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Articles similaires</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((relArticle) => (
                  <Card key={relArticle.id} className="overflow-hidden hover:shadow-2xl transition-all border-2 hover:border-emerald-500 group">
                    <div className="h-40 overflow-hidden">
                      <img 
                        src={relArticle.image} 
                        alt={relArticle.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                        width="400"
                        height="160"
                      />
                    </div>
                    
                    <CardContent className="p-6">
                      <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-medium">
                        {relArticle.category}
                      </span>
                      
                      <h3 className="font-bold text-lg text-blue-900 my-3 line-clamp-2">
                        {relArticle.title}
                      </h3>
                      
                      <p className="text-gray-600 mb-4 line-clamp-2 text-sm">
                        {relArticle.excerpt}
                      </p>
                      
                      <Link 
                        to={`/blog/${relArticle.slug}`}
                        className="text-emerald-600 hover:text-emerald-700 font-medium text-sm"
                      >
                        Lire l'article →
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;